import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaChevronRight,
    FaArrowUp,
    FaArrowDown,
    FaMagnifyingGlass,
    FaFilter,
    FaReceipt,
    FaCalendarDays,
    FaCircleInfo,
    FaSort,
    FaArrowRightArrowLeft,
    FaArrowLeft,
    FaXmark,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../../components/buttons/BtnDefault";
import { BtnBack } from "../../../../../components/buttons/BtnBack";
import { useHookPiutangTransaction } from "../../../../../hooks/useHookPiutangTransaction";
import { PiutangUpdate } from "./update";
import { FaEdit } from "react-icons/fa";

/* ============================================
   UTILITIES
   ============================================= */
const formatRupiah = (num) => {
    return new Intl.NumberFormat("id-ID").format(num);
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

/* ============================================
   KOMPONEN: Skeleton
   ============================================= */
const SkeletonRow = ({ cols = 7 }) => (
    <tr className="animate-pulse">
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} className="px-4 py-3.5">
                <div
                    className="h-3.5 bg-slate-700/60 rounded-md"
                    style={{ width: `${35 + Math.random() * 55}%` }}
                />
            </td>
        ))}
    </tr>
);

const SkeletonCard = () => (
    <div
        className="rounded-2xl overflow-hidden animate-pulse"
        style={{
            background: "rgba(15, 23, 42, 0.5)",
            border: "1px solid rgba(51, 65, 85, 0.3)",
        }}
    >
        <div
            className="px-4 py-3 flex justify-between items-center"
            style={{
                background: "rgba(15, 23, 42, 0.7)",
                borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
            }}
        >
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-slate-700/60" />
                <div className="h-3 bg-slate-700/50 rounded-md w-28" />
            </div>
            <div className="h-6 bg-slate-700/40 rounded-full w-16" />
        </div>
        <div className="p-4">
            <div className="flex justify-between items-start mb-3">
                <div className="space-y-2 flex-1">
                    <div className="h-5 bg-slate-700/60 rounded-md w-40" />
                    <div className="h-3 bg-slate-700/30 rounded-md w-24" />
                </div>
                <div className="text-right space-y-1">
                    <div className="h-2.5 bg-slate-700/30 rounded w-10 ml-auto" />
                    <div className="h-7 bg-slate-700/50 rounded-md w-28 ml-auto" />
                </div>
            </div>
            <div
                className="flex justify-between items-center pt-3"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
            >
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded bg-slate-700/50" />
                    <div className="h-3 bg-slate-700/30 rounded w-10" />
                </div>
                <div className="h-3 bg-slate-700/30 rounded w-20" />
            </div>
        </div>
    </div>
);

/* ============================================
   KOMPONEN: Tipe Badge
   ============================================= */
const TipeBadge = ({ tipe }) => {
    // tipe di sini diharapkan string "masuk" atau "keluar"
    const isMasuk = tipe === "masuk";
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${isMasuk
                    ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
                    : "bg-red-500/12 text-red-400 border border-red-500/20"
                }`}
        >
            {isMasuk ? (
                <FaArrowDown className="text-[10px]" />
            ) : (
                <FaArrowUp className="text-[10px]" />
            )}
            {isMasuk ? "Masuk" : "Keluar"}
        </span>
    );
};

/* ============================================
   KOMPONEN: Metode Badge
   ============================================= */
const MetodeBadge = ({ metode }) => {
    const colorMap = {
        "transfer": "text-blue-400 bg-blue-500/10 border-blue-500/20",
        "cash": "text-amber-400 bg-amber-500/10 border-amber-500/20",
        // Tambahkan metode lain sesuai kebutuhan
    };
    const lowerMetode = metode?.toLowerCase() || "";
    const color = colorMap[lowerMetode] || "text-slate-400 bg-slate-500/10 border-slate-500/20";
    
    return (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-medium border ${color}`}
        >
            {metode || "-"}
        </span>
    );
};

/* ============================================
   KOMPONEN: Select Styled
   ============================================= */
const SelectStyled = ({ value, onChange, options, icon, className }) => (
    <div className={`relative ${className}`}>
        {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">
                {icon}
            </div>
        )}
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full appearance-none text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all pr-8 ${icon ? "pl-9 py-2.5" : "pl-3 py-2.5"
                }`}
        >
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">
            <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    </div>
);

/* ============================================
   KOMPONEN: Filter Modal (POPUP)
   ============================================= */
const FilterModal = ({ isOpen, onClose, currentParams, onApply }) => {
    
    // State lokal sementara di dalam modal
    const [localFilters, setLocalFilters] = useState({
        type: currentParams.type || "",
        payment_method: currentParams.payment_method || "",
        start_date: currentParams.start_date || "",
        end_date: currentParams.end_date || "",
        created_by: currentParams.created_by || "",
    });
    
    // Reset ke params yang sedang aktif saat modal dibuka ulang (opsional)
    useEffect(() => {
        if (isOpen) {
            setLocalFilters({
                type: currentParams.type || "",
                payment_method: currentParams.payment_method || "",
                start_date: currentParams.start_date || "",
                end_date: currentParams.end_date || "",
                created_by: currentParams.created_by || "",
            });
        }
    }, [isOpen, currentParams]);
    
    if (!isOpen) return null;
    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setLocalFilters({
            type: "",
            payment_method: "",
            start_date: "",
            end_date: "",
            created_by: "",
        });
    };

    const handleSubmit = () => {
        onApply(localFilters);
        onClose();
    };

    // Options sesuai data JSON (misal: cash, transfer)
    const typeOptions = [
        { value: "", label: "Semua Tipe" },
        { value: "masuk", label: "Pemasukan" },
        { value: "keluar", label: "Pengeluaran" },
    ];

    const paymentOptions = [
        { value: "", label: "Semua Metode" },
        { value: "cash", label: "Cash" },
        { value: "transfer", label: "Transfer" },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div
                className="relative w-full max-w-md bg-[#1e293b] rounded-2xl shadow-2xl border border-slate-700 overflow-hidden animate-[fadeIn_0.2s_ease-out]"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/50">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <FaFilter className="text-cyan-400 text-sm" />
                        Filter Transaksi
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-700"
                    >
                        <FaXmark className="text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Tipe */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                            Tipe Transaksi
                        </label>
                        <SelectStyled
                            name="type"
                            value={localFilters.type}
                            onChange={handleChange}
                            options={typeOptions}
                        />
                    </div>

                    {/* Metode Pembayaran */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                            Metode Pembayaran
                        </label>
                        <SelectStyled
                            name="payment_method"
                            value={localFilters.payment_method}
                            onChange={handleChange}
                            options={paymentOptions}
                        />
                    </div>

                    {/* Rentang Tanggal */}
                    <div className="space-y-3">
                        <label className="block text-xs font-semibold text-slate-400 mb-1 uppercase tracking-wider">
                            Rentang Tanggal
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <span className="text-[10px] text-slate-500 block mb-1">Dari</span>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={localFilters.start_date}
                                    onChange={handleChange}
                                    className="w-full text-sm bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-white focus:border-cyan-500 outline-none [color-scheme:dark]"
                                />
                            </div>
                            <div>
                                <span className="text-[10px] text-slate-500 block mb-1">Sampai</span>
                                <input
                                    type="date"
                                    name="end_date"
                                    value={localFilters.end_date}
                                    onChange={handleChange}
                                    className="w-full text-sm bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-white focus:border-cyan-500 outline-none [color-scheme:dark]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Created By */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                            Dibuat Oleh (ID/Nama)
                        </label>
                        <input
                            type="text"
                            name="created_by"
                            value={localFilters.created_by}
                            onChange={handleChange}
                            placeholder="Cari nama pembuat..."
                            className="w-full text-sm bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-white placeholder-slate-500 focus:border-cyan-500 outline-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 bg-slate-800/50 border-t border-slate-700/50 flex justify-between gap-3">
                    <button
                        onClick={handleReset}
                        className="px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                        Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="flex-1 px-4 py-2 rounded-xl text-sm font-bold bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg shadow-cyan-900/20 transition-all"
                    >
                        Terapkan Filter
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ============================================
   KOMPONEN: Ringkasan Card
   ============================================= */
const SummaryCards = ({ data }) => {
    // Perbaikan: akses item.type.name karena type sekarang object
    const totalPayment = data
        .filter((d) => d.type?.name === "masuk")
        .reduce((sum, d) => sum + Number(d.nominal), 0);
    const totalPiutang = data
        .filter((d) => d.type?.name === "keluar")
        .reduce((sum, d) => sum + Number(d.nominal), 0);
    const remainingPayment = totalPayment - totalPiutang;
    const trxCount = data.length;

    const cards = [
        {
            label: "Total Pengeluaran",
            value: totalPiutang,
            color: "text-red-400",
            bgIcon: "bg-red-500/10 border-red-500/20",
            icon: <FaArrowUp className="text-red-400 text-sm" />,
            prefix: "-",
            isCount: false,
        },
        {
            label: "Total Pembayaran",
            value: totalPayment,
            color: "text-emerald-400",
            bgIcon: "bg-emerald-500/10 border-emerald-500/20",
            icon: <FaArrowDown className="text-emerald-400 text-sm" />,
            prefix: "+",
            isCount: false,
        },
        {
            label: "Sisa Piutang",
            value: remainingPayment,
            color: remainingPayment >= 0 ? "text-cyan-400" : "text-red-400",
            bgIcon:
                remainingPayment >= 0
                    ? "bg-cyan-500/10 border-cyan-500/20"
                    : "bg-red-500/10 border-red-500/20",
            icon: (
                <FaArrowRightArrowLeft
                    className={`text-sm ${remainingPayment >= 0 ? "text-cyan-400" : "text-red-400"}`}
                />
            ),
            prefix: remainingPayment >= 0 ? "+" : "",
            isCount: false,
        },
        {
            label: "Jumlah Transaksi",
            value: trxCount,
            color: "text-violet-400",
            bgIcon: "bg-violet-500/10 border-violet-500/20",
            icon: <FaReceipt className="text-violet-400 text-sm" />,
            prefix: "",
            isCount: true,
        },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((card) => (
                <div
                    key={card.label}
                    className="rounded-2xl p-4 transition-all duration-200"
                    style={{
                        background: "rgba(15, 23, 42, 0.5)",
                        border: "1px solid rgba(51, 65, 85, 0.3)",
                    }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            {card.label}
                        </span>
                        <div className={`p-2 rounded-xl border ${card.bgIcon}`}>
                            {card.icon}
                        </div>
                    </div>
                    <p className={`text-xl lg:text-2xl font-extrabold ${card.color} leading-tight`}>
                        {card.prefix}
                        {card.isCount ? card.value : formatRupiah(card.value)}
                    </p>
                </div>
            ))}
        </div>
    );
};

/* ============================================
   KOMPONEN: Pagination
   ============================================= */
const Pagination = ({ meta, onPageChange }) => {
    if (!meta || meta.last_page <= 1) return null;

    const { current_page, last_page, from, to, total } = meta;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, current_page - Math.floor(maxVisible / 2));
        let end = Math.min(last_page, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push("...");
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < last_page) {
            if (end < last_page - 1) pages.push("...");
            pages.push(last_page);
        }

        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
            <p className="text-xs text-slate-500">
                Menampilkan <span className="text-slate-400 font-medium">{from ?? 0}</span>{" "}
                - <span className="text-slate-400 font-medium">{to ?? 0}</span>{" "}
                dari <span className="text-slate-400 font-medium">{total ?? 0}</span> data
            </p>
            <div className="flex items-center gap-1.5">
                <button
                    onClick={() => onPageChange(current_page - 1)}
                    disabled={current_page <= 1}
                    className="p-2 rounded-lg border border-slate-600/40 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <FaArrowLeft className="text-xs" />
                </button>
                {getPageNumbers().map((page, idx) =>
                    page === "..." ? (
                        <span key={`dots-${idx}`} className="px-2 text-slate-500 text-xs">
                            ...
                        </span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-all ${page === current_page
                                    ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                                    : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 border border-transparent"
                                }`}
                        >
                            {page}
                        </button>
                    )
                )}
                <button
                    onClick={() => onPageChange(current_page + 1)}
                    disabled={current_page >= last_page}
                    className="p-2 rounded-lg border border-slate-600/40 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <FaChevronRight className="text-xs" />
                </button>
            </div>
        </div>
    );
};

/* ============================================
   CONSTANTS (Options)
   ============================================= */
const SORT_OPTIONS = [
    { value: "", label: "Default" },
    { value: "date_time-desc", label: "Terbaru" },
    { value: "date_time-asc", label: "Terlama" },
    { value: "nominal-desc", label: "Nominal Terbesar" },
    { value: "nominal-asc", label: "Nominal Terkecil" },
];

const LIMIT_OPTIONS = [
    { value: "5", label: "5" },
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "50", label: "50" },
];

/* ============================================
   MAIN: Transaction
   ============================================= */
export const PiutangTransaction = () => {
    const [showSidebar, setShowSidebar] = useState(true);
    const navigate = useNavigate();
    const { user_id: userId } = useParams();
    const [selectedIds, setSelectedIds] = useState([]);
    const [openModalCreate, setOpenModalCreate] = useState(false);

    const [openModalUpdate, setOpenModalUpdate] = useState(false);
    const [dataUpdate, setDataUpdate] = useState(null);

    // State untuk Modal Filter
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // ✅ AMBIL SEMUA DARI HOOK
    const {
        data,
        meta,
        loading,
        error,
        params,
        updateParams,
        resetParams,
        refreshData,
    } = useHookPiutangTransaction(userId);

    // ✅ Helper: update params dan reset ke page 1
    const handleFilterChange = (newParams) => {
        updateParams({ ...newParams, page: 1 });
    };

    // ✅ Helper khusus ganti halaman
    const handlePageChange = (page) => {
        updateParams({ page });
    };

    // ✅ Handle Apply dari Modal Filter
    const handleApplyFilter = (filters) => {
        handleFilterChange(filters);
    };

    // ✅ Hitung jumlah filter aktif untuk badge notifikasi
    const activeFilterCount = [
        params.type, 
        params.payment_method, 
        params.start_date, 
        params.end_date,
        params.created_by
    ].filter(Boolean).length;

    // ✅ Cek apakah ada search aktif
    const hasActiveSearch = Boolean(params.search);

    /* ---------- Column Config ---------- */
    const columns = [
        { key: "no", label: "No", align: "center", width: "55px" },
        { key: "action", label: "Aksi", align: "center", },
        { key: "tipe", label: "Tipe", align: "center", width: "110px" },
        { key: "tanggal", label: "Tanggal", align: "left" },
        { key: "category", label: "Kategori", align: "center", width: "110px" },
        { key: "code", label: "Kode", align: "left" },
        { key: "nominal", label: "Nominal", align: "right" },
        { key: "metode", label: "Metode", align: "center", width: "130px" },
        { key: "keterangan", label: "Keterangan", align: "left" },
    ];

    // Toggle select all
    const toggleSelectAll = () => {
        if (selectedIds.length === data?.length && data?.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(data?.map((item) => item.id) ?? []);
        }
    };

    // Toggle select single
    const toggleSelectSingle = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    return (
        <>
            <Helmet>
                <title>Transaksi Keuangan - DANELA NET</title>
                <meta
                    name="description"
                    content="Kelola data transaksi keuangan, pantau pemasukan dan pengeluaran, serta filter berdasarkan tipe dan metode pembayaran."
                />
            </Helmet>

            <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
                {/* ===== Header ===== */}
                <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
                    <div className="flex items-center gap-3">
                        <BtnBack />
                        <h2 className="text-base md:text-xl font-bold text-gray-100">
                            Transaksi Keuangan
                        </h2>
                        <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                            {meta?.total ?? 0} data
                        </span>
                    </div>
                    <BtnDefault onClick={() => setOpenModalCreate(true)}>
                        <span>Tambah Data</span>
                    </BtnDefault>
                </Header>

                {/* ===== Content ===== */}
                <main className="p-4 lg:p-6 space-y-5">
                    {/* ===== Summary Cards ===== */}
                    {!loading && data?.length > 0 && <SummaryCards data={data} />}

                    {/* ===== Search & Filter Bar (Simplified) ===== */}
                    <section
                        className="rounded-2xl p-4 sm:p-5"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                    >
                        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                            
                            {/* Left: Search & Sort */}
                            <div className="w-full lg:flex-1 flex flex-col sm:flex-row gap-3">
                                <div className="relative w-full sm:max-w-xs">
                                    <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                                    <input
                                        type="text"
                                        placeholder="Cari kode, deskripsi..."
                                        value={params.search}
                                        onChange={(e) => handleFilterChange({ search: e.target.value })}
                                        className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 transition-all"
                                    />
                                </div>
                                
                                <div className="w-full sm:w-44">
                                    <SelectStyled
                                        value={params.sort}
                                        onChange={(val) => handleFilterChange({ sort: val })}
                                        options={SORT_OPTIONS}
                                        icon={<FaSort />}
                                    />
                                </div>
                            </div>

                            {/* Right: Action Buttons */}
                            <div className="flex items-center gap-3 w-full lg:w-auto justify-end">
                                <button
                                    onClick={() => setIsFilterModalOpen(true)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                                        activeFilterCount > 0
                                            ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400"
                                            : "bg-slate-800/60 border-slate-600/40 text-slate-300 hover:bg-slate-700"
                                    }`}
                                >
                                    <div className="relative">
                                        <FaFilter />
                                        {activeFilterCount > 0 && (
                                            <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-cyan-500 text-[9px] font-bold text-white">
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </div>
                                    Filter
                                </button>

                                <div className="w-28">
                                    <SelectStyled
                                        value={String(params.per_page)}
                                        onChange={(val) => handleFilterChange({ per_page: Number(val) })}
                                        options={LIMIT_OPTIONS}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quick Active Filters (Chips) */}
                        {activeFilterCount > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-700/30">
                                <span className="text-xs text-slate-500">Filter aktif:</span>
                                {params.type && (
                                    <span className="px-2 py-1 rounded-md bg-slate-700 text-slate-300 text-xs border border-slate-600">
                                        Tipe: {params.type}
                                    </span>
                                )}
                                {params.payment_method && (
                                    <span className="px-2 py-1 rounded-md bg-slate-700 text-slate-300 text-xs border border-slate-600">
                                        Metode: {params.payment_method}
                                    </span>
                                )}
                                {(params.start_date || params.end_date) && (
                                    <span className="px-2 py-1 rounded-md bg-slate-700 text-slate-300 text-xs border border-slate-600">
                                        {params.start_date} s/d {params.end_date || "..."}
                                    </span>
                                )}
                                <button
                                    onClick={() => {
                                        resetParams();
                                        // Reset halaman ke 1 saat reset
                                        handleFilterChange({ page: 1 });
                                    }}
                                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                                >
                                    <FaXmark /> Hapus Semua
                                </button>
                            </div>
                        )}
                    </section>

                    {/* ===== Error State ===== */}
                    {error && (
                        <div
                            className="rounded-2xl p-4 flex items-center gap-3"
                            style={{
                                background: "rgba(239, 68, 68, 0.08)",
                                border: "1px solid rgba(239, 68, 68, 0.2)",
                            }}
                        >
                            <FaCircleInfo className="text-red-400 flex-shrink-0" />
                            <div>
                                <p className="text-red-400 text-sm font-medium">Gagal memuat data</p>
                                <p className="text-red-400/60 text-xs mt-0.5">
                                    {error?.message || "Terjadi kesalahan pada server"}
                                </p>
                            </div>
                            <button
                                onClick={refreshData}
                                className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                            >
                                Coba Lagi
                            </button>
                        </div>
                    )}

                    {/* ===== Desktop Table ===== */}
                    <section
                        className="hidden lg:block rounded-2xl overflow-hidden"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr
                                        className="border-b"
                                        style={{
                                            background: "rgba(15, 23, 42, 0.7)",
                                            borderColor: "rgba(51, 65, 85, 0.3)",
                                        }}
                                    >
                                        <th className="px-4 py-3.5 text-center w-12">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.length === data?.length && data?.length > 0}
                                                onChange={toggleSelectAll}
                                                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 cursor-pointer"
                                            />
                                        </th>
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center"
                                                        ? "text-center"
                                                        : col.align === "right"
                                                            ? "text-right"
                                                            : "text-left"
                                                    }`}
                                                style={col.width ? { width: col.width } : undefined}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                                    {loading &&
                                        [...Array(params.per_page)].map((_, i) => (
                                            <SkeletonRow key={`skel-${i}`} cols={columns.length + 1} />
                                        ))}

                                    {!loading && data?.length === 0 && (
                                        <tr>
                                            <td colSpan={columns.length + 1} className="px-4 py-16 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-slate-800 border border-slate-700">
                                                        <FaReceipt className="text-2xl text-slate-600" />
                                                    </div>
                                                    <p className="text-slate-400 font-medium">Tidak ada transaksi ditemukan</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading &&
                                        data?.map((item, index) => (
                                            <tr
                                                key={item.id}
                                                className="cursor-pointer transition-colors duration-150 hover:bg-cyan-500/5"
                                            >
                                                <td className="px-4 py-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(item.id)}
                                                        onChange={() => toggleSelectSingle(item.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 cursor-pointer"
                                                    />
                                                </td>

                                                <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                                                    {(meta?.from ?? 0) + index}
                                                </td>
                                                
                                                <td className="px-4 py-3 text-center">
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            setDataUpdate(item);
                                                            setOpenModalUpdate(true);
                                                        }}
                                                        className="p-2 rounded-lg text-white bg-yellow-500/30 hover:bg-yellow-500/70 transition-all"
                                                    >
                                                        <FaEdit className="text-xs" />
                                                    </button>
                                                </td>

                                                {/* FIX: Access item.type.name */}
                                                <td className="px-4 py-3 text-center">
                                                    <TipeBadge tipe={item.type?.name} />
                                                </td>

                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <FaCalendarDays className="text-xs text-slate-500" />
                                                        <span className="text-gray-200 text-sm">
                                                            {formatDate(item.date_time)}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* FIX: Access item.category.name */}
                                                <td className="px-4 py-3 text-center min-w-32">
                                                    {item.category?.name || "-"}
                                                </td>

                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">
                                                        {item.code}
                                                    </span>
                                                </td>

                                                <td className="px-4 py-3 text-right">
                                                    <span className={`font-bold ${item.type?.name === "masuk" ? "text-emerald-400" : "text-red-400"}`}>
                                                        {item.type?.name === "masuk" ? "+" : "-"}
                                                        {formatRupiah(item.nominal)}
                                                    </span>
                                                </td>

                                                {/* FIX: Access item.payment_method.name */}
                                                <td className="px-4 py-3 text-center">
                                                    <MetodeBadge metode={item.payment_method?.name} />
                                                </td>

                                                <td className="px-4 py-3">
                                                    <span className="text-slate-300 text-sm line-clamp-2">
                                                        {item.description || "-"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {!loading && data?.length > 0 && (
                            <div className="px-4 pb-4">
                                <Pagination meta={meta} onPageChange={handlePageChange} />
                            </div>
                        )}
                    </section>

                    {/* ===== Mobile Cards ===== */}
                    <section className="lg:hidden space-y-3">
                        {loading &&
                            [...Array(params.per_page)].map((_, i) => (
                                <SkeletonCard key={`skel-card-${i}`} />
                            ))}

                        {!loading && data?.length === 0 && (
                            <div className="rounded-2xl p-8 text-center bg-slate-800/50 border border-slate-700">
                                <FaReceipt className="text-3xl text-slate-600 mx-auto mb-3" />
                                <p className="text-slate-400 font-medium">Tidak ada transaksi ditemukan</p>
                            </div>
                        )}

                        {!loading &&
                            data?.map((item, index) => {
                                const isMasuk = item.type?.name === "masuk";
                                return (
                                    <div
                                        key={item.id}
                                        className="rounded-2xl overflow-hidden bg-slate-800/40 border border-slate-700/50"
                                    >
                                        <div className="px-4 py-3 flex justify-between items-center bg-slate-800/60 border-b border-slate-700/30">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs text-slate-500">
                                                    #{(meta?.from ?? 0) + index}
                                                </span>
                                                <TipeBadge tipe={item.type?.name} />
                                            </div>
                                            <MetodeBadge metode={item.payment_method?.name} />
                                        </div>

                                        <div className="p-4">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="space-y-2 flex-1 min-w-0">
                                                    <p className="text-gray-200 text-sm font-medium truncate">
                                                        {item.category?.name || "-"}
                                                    </p>
                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                        <FaCalendarDays />
                                                        <span>{formatDate(item.date_time)}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right ml-3">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-0.5">Nominal</span>
                                                    <p className={`text-lg font-extrabold ${isMasuk ? "text-emerald-400" : "text-red-400"}`}>
                                                        {isMasuk ? "+" : "-"}
                                                        {formatRupiah(item.nominal)}
                                                    </p>
                                                </div>
                                            </div>

                                            {item.description && (
                                                <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                                                    {item.description}
                                                </p>
                                            )}

                                            <div className="flex justify-between items-center pt-3 border-t border-slate-700/30">
                                                <span className="font-mono text-xs text-cyan-400/70 bg-cyan-500/8 px-2 py-0.5 rounded">
                                                    {item.code}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        setDataUpdate(item);
                                                        setOpenModalUpdate(true);
                                                    }}
                                                    className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"
                                                >
                                                    <FaEdit /> Edit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                        {!loading && data?.length > 0 && (
                            <Pagination meta={meta} onPageChange={handlePageChange} />
                        )}
                    </section>
                </main>
            </DashboardLayout>

            {/* ===== Filter Modal ===== */}
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                currentParams={params}
                onApply={handleApplyFilter}
            />

            {/* ===== Modal Update ===== */}
            <PiutangUpdate
                open={openModalUpdate}
                onClose={() => setOpenModalUpdate(false)}
                data={dataUpdate}
                onSuccess={() => {
                    setOpenModalUpdate(false);
                    refreshData();
                }}
            />
        </>
    );
};